import { decamelizeKeys } from "humps";
import { sortBy } from "lodash";
import moment from "moment";
import AppContext from "PFApp/app_context";
import { activityNotificationTypes } from "PFCore/helpers/activities";
import { useEngagements } from "PFCore/hooks/queries/engagements/use_engagements";
import { useNotifications } from "PFCore/hooks/queries/notifications/use_notifications";
import { useNotificationsInvalidate } from "PFCore/hooks/queries/notifications/use_notifications_invalidate";
import { useRoles } from "PFCore/hooks/queries/roles/use_roles";
import { NOTIFICATIONS_ASYNC_ACTIONS } from "PFReducers/notifications_reducer";
import { Activity, Notification } from "PFTypes";
import { useCallback, useContext, useMemo } from "react";

type ActivitiesWithNotifications = (Activity & { notifications: Notification[] })[];
type UseInboxReturn = {
  activitiesWithNotifications: ActivitiesWithNotifications;
  markAsRead: (activityId: number) => Promise<void | null>;
  isLoading: boolean;
};

const PER_PAGE = 100;

export const useInbox = (templateKey: string): UseInboxReturn => {
  const { dispatch } = useContext(AppContext);
  const { invalidate: invalidateNotifications } = useNotificationsInvalidate();

  const notificationsParams = useMemo(
    () => ({
      read: false,
      perPage: PER_PAGE,
      templateKey,
      targetTypes: activityNotificationTypes
    }),
    [templateKey]
  );

  const markAsRead = useCallback(
    (activityId: number): Promise<void | null> =>
      (
        dispatch({
          type: NOTIFICATIONS_ASYNC_ACTIONS.BULK_MARK_AS_READ,
          payload: {
            activity_id: activityId,
            target_types: activityNotificationTypes
          }
        }) as Promise<object>
      ).then(
        () => invalidateNotifications(notificationsParams),
        () => null
      ),
    [dispatch, notificationsParams, invalidateNotifications]
  );

  const { data: notifications, isLoading: isNotificationsLoading } = useNotifications(notificationsParams, {
    keepPreviousData: true
  });

  const notificationsGrouped = useMemo(
    () =>
      decamelizeKeys(
        ((notifications?.entries || []) as Notification[]).reduce((previous, current) => {
          const { activityId } = current;
          if (activityId) {
            if (previous[activityId]) {
              previous[activityId].push(current);
            } else {
              previous[activityId] = [current];
            }
          }
          return previous;
        }, {})
      ),
    [notifications]
  );

  const activitiesIds = notificationsGrouped && (Object.keys(notificationsGrouped) as unknown as number[]);

  const { data: roles, isLoading: isRolesLoading } = useRoles(
    { filters: { fields: { id: activitiesIds } }, perPage: PER_PAGE, page: 1 },
    { enabled: templateKey === "role" && activitiesIds?.length > 0, keepPreviousData: true }
  );

  const { data: engagements, isLoading: isEngagementsLoading } = useEngagements(
    { filters: { fields: { id: activitiesIds } }, perPage: PER_PAGE, page: 1 },
    { enabled: templateKey === "engagement" && activitiesIds?.length > 0, keepPreviousData: true }
  );

  const isLoading: boolean =
    isNotificationsLoading ||
    { role: isRolesLoading, engagement: isEngagementsLoading }[templateKey] ||
    false;

  const activitiesWithNotifications: ActivitiesWithNotifications = useMemo(() => {
    const collection = {
      role: roles,
      engagement: engagements
    }[templateKey];

    if (!collection) {
      return [];
    }

    return sortBy(
      collection.entries.map(({ activity, id }) => ({
        ...decamelizeKeys(activity),
        notifications: notificationsGrouped[id]
      })) as ActivitiesWithNotifications,
      ({ post_until }) => Math.min(moment(post_until).unix() - moment().unix(), 1)
    );
  }, [templateKey, roles, engagements, notificationsGrouped]);

  return { activitiesWithNotifications, markAsRead, isLoading };
};
